import React, {
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
  useRef,
} from 'react';

import { useMergedRefs } from '../../hooks/useMergedRefs';
import { useProperFocus } from '../../hooks/useProperFocus';
import { cn } from '../../lib/utils';
import { type CSSProperties } from '../../style/scrollbars';

// Preserved for components that still reference defaultInputStyle.
// Maps to the same look the new Tailwind classes produce.
export const defaultInputStyle = {
  outline: 0,
  backgroundColor: 'var(--color-tableBackground)',
  color: 'var(--color-formInputText)',
  margin: 0,
  padding: 5,
  borderRadius: 6,
  border: '1px solid var(--color-formInputBorder)',
};

// Visual chrome via Tailwind / shadcn tokens — adapts to light/dark.
// Pseudo-selectors (:focus, ::placeholder) handled via Tailwind variants.
const inputClasses = cn(
  'block whitespace-nowrap overflow-hidden shrink-0',
  'm-0 px-2 py-1.5 rounded-md',
  'bg-background text-foreground text-[13px] leading-tight',
  'border border-input',
  'transition-[box-shadow,border-color] duration-150',
  'placeholder:text-muted-foreground',
  'focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40',
  'disabled:opacity-50 disabled:cursor-not-allowed',
);

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  style?: CSSProperties;
  inputRef?: Ref<HTMLInputElement>;
  onEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onEscape?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onChangeValue?: (newValue: string) => void;
  onUpdate?: (newValue: string) => void;
  focused?: boolean;
};

export function Input({
  style,
  inputRef,
  onEnter,
  onEscape,
  onChangeValue,
  onUpdate,
  focused,
  className,
  ...nativeProps
}: InputProps) {
  const ref = useRef<HTMLInputElement>(null);
  useProperFocus(ref, focused);

  const mergedRef = useMergedRefs<HTMLInputElement>(ref, inputRef);

  return (
    <input
      ref={mergedRef}
      className={cn(inputClasses, className)}
      style={style}
      {...nativeProps}
      onKeyDown={e => {
        nativeProps.onKeyDown?.(e);

        if (e.key === 'Enter' && onEnter) {
          onEnter(e);
        }

        if (e.key === 'Escape' && onEscape) {
          onEscape(e);
        }
      }}
      onBlur={e => {
        onUpdate?.(e.target.value);
        nativeProps.onBlur?.(e);
      }}
      onChange={e => {
        onChangeValue?.(e.target.value);
        nativeProps.onChange?.(e);
      }}
    />
  );
}

const bigInputClasses = cn(
  'px-3 py-2.5 text-[15px] border-0 shadow-sm',
  'focus:border-0 focus:shadow-md',
);

export function BigInput(props: InputProps) {
  return (
    <Input
      {...props}
      className={cn(bigInputClasses, props.className)}
    />
  );
}
